import { HttpClient } from '@angular/common/http';
import { ExportLogErrorStatus, LogType } from 'src/app/enums/export-log.enum';
import { environment } from 'src/environments/environment';
import { LoggerService } from '../logger/logger.service';
import { MatDialog, MatDialogRef } from '@lenovo/material/dialog';
import { VantageShellService } from '../vantage-shell/vantage-shell.service';

declare let window;
export abstract class CommonExportLogService {
	protected currentLogType: LogType;

	// Application attributes
	protected experienceVersion: string;
	protected shellVersion: string;
	protected bridgeVersion: string;

	// Machine attributes
	protected machineModel: string;
	protected serialNumber: string;
	protected biosVersion: string;
	protected productName: string;

	// Atributes to html
	protected document: HTMLDocument;

	constructor(
		protected dialog: MatDialog,
		protected http: HttpClient,
		protected logger: LoggerService,
		protected shellService: VantageShellService
	) {
		if (window.Windows) {
			const packageVersion = window.Windows.ApplicationModel.Package.current.id.version;
			this.shellVersion = `${packageVersion.major}.${packageVersion.minor}.${packageVersion.build}.${packageVersion.revision}`;
		}

		const jsBridgeVersion = this.shellService.getVersion();
		if (
			document.location.href.indexOf('stage') >= 0 ||
			document.location.href.indexOf('vantage.csw.') >= 0
		) {
			this.bridgeVersion = jsBridgeVersion ? jsBridgeVersion.split('-')[0] : '';
		} else {
			this.bridgeVersion = jsBridgeVersion ? jsBridgeVersion : '';
		}

		this.experienceVersion = environment.appVersion;

		// Consult Machine Information
		try {
			this.shellService
				.getSysinfo()
				.getMachineInfo()
				.then((info) => {
					this.machineModel = info?.family;
					this.serialNumber = info?.serialnumber;
					this.biosVersion = info?.biosVersion;
					this.productName = info?.mtm;
				});
		} catch (error) {
			logger.error('[Export Log] Could not load environment info', error);
		}
	}

	/**
	 * Function that will export a log file
	 *
	 * @param logType value to differenciate log type
	 */
	public async exportLog(logType: LogType): Promise<[ExportLogErrorStatus, string]> {
		try {
			this.currentLogType = logType;
			const reportFileName = logType.valueOf();

			const timeoutPromise = new Promise((resolve, reject) => {
				const timeout = setTimeout(() => {
					clearTimeout(timeout);
					reject('Timed out after 10s when tried to prepareData to export log!');
				}, 10000);
			});

			const dataPrepared = await Promise.race([this.prepareData(), timeoutPromise]);
			const dataFormatted = await this.generateHtmlReport(dataPrepared);

			const createdFilePath = await this.exportReportToFile(reportFileName, dataFormatted);
			return [ExportLogErrorStatus.SuccessExport, createdFilePath];
		} catch (error) {
			this.logger.error('[Export Log] Could not export log', error);
			throw ExportLogErrorStatus.GenericError;
		}
	}

	public getExportIcon(isDisabled: boolean): string {
		if (isDisabled) {
			return 'assets/icons/hardware-scan/icon_hardware_export-log_disabled.svg';
		} else {
			return 'assets/icons/hardware-scan/icon_hardware_export-log.svg';
		}
	}

	protected openExportLogComponentsModal(modalComponent: any): MatDialogRef<any> {
		const modal = this.dialog.open(modalComponent, {
			autoFocus: false,
			hasBackdrop: true,
			disableClose: true,
			panelClass: 'export-modal-size',
		});

		this.updateExportLogComponentsModal(modal);

		return modal;
	}

	protected updateExportLogComponentsModal(
		modal: MatDialogRef<any>,
		error: ExportLogErrorStatus = ExportLogErrorStatus.LoadingExport,
		logPath: string = ''
	) {
		(modal.componentInstance as any).logPath = logPath;
		(modal.componentInstance as any).errorStatus = error;
	}

	private async exportReportToFile(reportFileName: string, dataFormatted: any): Promise<string> {
		let pathSaved = '';
		const fileName = reportFileName + '_' + this.getDateAndHour();
		const picker = new window.Windows.Storage.Pickers.FileSavePicker();
		picker.suggestedFileName = fileName;

		// Display html extension in file picker
		picker.fileTypeChoices.insert('Hyper Text Markup Language File', ['.html']);
		const file = await picker.pickSaveFileAsync();
		if (file !== null) {
			await window.Windows.Storage.FileIO.writeTextAsync(file, dataFormatted);
			pathSaved = file.path;
		} else {
			this.logger.info('[exportReportToFile] File is null');
		}

		if (pathSaved === '') {
			throw new Error('[exportReportToFile] Operation cancelled');
		}

		return pathSaved;
	}

	private getDateAndHour(): string {
		const date = new Date();
		const day = date.getDate().toString();
		const maskDay = day.length === 1 ? '0' + day : day;
		const month = (date.getMonth() + 1).toString();
		const maskMonth = month.length === 1 ? '0' + month : month;
		const year = date.getFullYear().toString();
		const time = date.toTimeString().split(' ');

		return year + maskMonth + maskDay + '_' + time[0].replace(/:/g, '');
	}

	private generateHtmlReport(jsonData: any): Promise<string> {
		let templatePath: string;

		switch (this.currentLogType) {
			case LogType.scan:
			case LogType.rbs:
				templatePath = 'assets/templates/hardware-scan/export-results-template.html';
				break;
			case LogType.snapshot:
				templatePath = 'assets/templates/snapshot/snapshot-results-template.html';
				break;
		}

		return new Promise((resolve, reject) => {
			this.http
				.get(templatePath, { responseType: 'text' })
				.toPromise()
				.then((htmlData) => {
					this.document = new DOMParser().parseFromString(htmlData, 'text/html');
					this.populateHtml(jsonData);
					resolve(this.document.documentElement.outerHTML);
				})
				.catch((error) => {
					this.logger.exception('[ExportResultService] generateHtmlReport', error);
					reject(error);
				});
		});
	}

	/**
	 * Function that will populate html template
	 *
	 * @param jsonData A json object containing all data needed to export the results
	 */
	protected abstract populateHtml(jsonData: any): void;

	/**
	 * Function that will get correct data and prepare it deppending on log type
	 */
	protected abstract prepareData(): Promise<any>;
}
